import { parseInt } from 'lodash'

export const AZURE = {
  clientId: process.env.AZURE_AD_CLIENT_ID ?? '',
  clientSecret: process.env.AZURE_AD_CLIENT_SECRET ?? '',
  tenantId: process.env.AZURE_AD_TENANT_ID ?? '',
  redirect_uri: `${process.env.NEXTAUTH_URL}/api/auth/callback/azure-ad`,
  scopes: {
    basic: ['email', 'openid', 'profile', 'offline_access'],
    v1: [
      'Chat.Create', // Create a new chat (TODO)
      'Chat.ReadBasic', // List chats in Dropdown
      'ChatMessage.Send', // Send messages to chats in behalf of the user
      'User.Read', // Sign in to the app and read signed-in user details
      'User.ReadBasic.All', // Get other user's profile photo
    ],
    v2: [
      'Team.ReadBasic.All', // Get teams in the organization
      'Channel.ReadBasic.All', // Get channels
      'ChannelMessage.Send', // // Send messages to channels in behalf of the user
    ],
  },
  getScopes: () =>
    [...AZURE.scopes.basic, ...AZURE.scopes.v1, ...AZURE.scopes.v2].join(' '),
  getV1Scopes: () => [...AZURE.scopes.basic, ...AZURE.scopes.v1].join(' '),
  canUseV2: (scope: string) => scope.includes('ChannelMessage.Send'),
}

const parseEnvInt = (key: string, def: number): number => {
  const text = process.env[key]
  if (!text) {
    return def
  }
  try {
    return parseInt(text)
  } catch (err) {}
  return def
}

export const APP_NAME = 'delayed-send'
export const VERSION = process.env.VERSION ?? 'unknown'
export const DEVELOPMENT = process.env.NODE_ENV === 'development'
export const MONGO_URI = process.env.MONGODB_URI ?? ''
export const TASK_AUTH_SECRET = process.env.TASK_AUTH_SECRET || ''

// Azure Communication Services configuration
// For backward compatibility, check both new and old env var names
export const COMMUNICATION_SERVICES_CONNECTION_STRING =
  process.env.COMMUNICATION_SERVICES_CONNECTION_STRING ||
  process.env.AZURE_COMMUNICATION_SERVICE_CONNECTION_STRING ||
  ''

export const COMMUNICATION_SERVICES_SENDER_ADDRESS =
  process.env.COMMUNICATION_SERVICES_SENDER_ADDRESS ||
  process.env.AZURE_COMMUNICATION_SERVICE_SENDER_ADDRESS ||
  'noreply@delayedsend.com'

export const COMMUNICATION_SERVICES_REPLY_TO =
  process.env.COMMUNICATION_SERVICES_REPLY_TO || 'noreply@delayedsend.com'

// Enable email service in development based on available configurations
export const DEV_EMAIL_SERVICE =
  process.env.NODE_ENV === 'production' ||
  !!COMMUNICATION_SERVICES_CONNECTION_STRING

// Azure Communication Services is the only email provider
export const USE_AZURE_COMMUNICATION_SERVICE = !!COMMUNICATION_SERVICES_CONNECTION_STRING

export const KEEP_ALIVE_REFRESH_TOKEN_THRESHOLD = parseEnvInt(
  'KEEP_ALIVE_REFRESH_TOKEN_THRESHOLD',
  10 * 60 * 60 * 24 * 1000
) //5 days in secs
export const KEEP_ALIVE_REFRESH_TOKEN_LIMIT = parseEnvInt(
  'KEEP_ALIVE_REFRESH_TOKEN_LIMIT',
  10
) //10 users

const nextAuth = process.env.NEXTAUTH_URL || ''
export const SAFE_COOKIE = nextAuth.indexOf('https://') == 0
export const TEAMS_APP_ID = process.env.TEAMS_APP_ID ?? ''

export const TEAMS = {
  DEEP_LINK_DASHBOARD: `https://teams.microsoft.com/l/entity/${TEAMS_APP_ID}/com.delayedsend.teams.main`,
}

export const FEATURE_FLAGS = {
  PREMIUM: {
    enabled: true,
  },
  // Only bypass if explicitly enabled via env var, not automatically in development
  BYPASS_SUBSCRIPTION_CHECKS: process.env.BYPASS_SUBSCRIPTION_CHECKS === 'true',
}

// Azure Blob Storage configuration
export const STORAGE_CONFIG = {
  AZURE: {
    connectionString: process.env.AZURE_STORAGE_CONNECTION_STRING || '',
    containerName: process.env.AZURE_STORAGE_CONTAINER_NAME || 'delayedsend-attachments',
    devConnectionString: process.env.DEV_AZURE_STORAGE_CONNECTION_STRING || process.env.AZURE_STORAGE_CONNECTION_STRING || '',
    devContainerName: process.env.DEV_AZURE_STORAGE_CONTAINER_NAME || 'delayedsend-dev-attachments',
    accountName: process.env.AZURE_STORAGE_ACCOUNT_NAME || '',
    accountKey: process.env.AZURE_STORAGE_ACCOUNT_KEY || '',
  },
}

export const SUBSCRIPTION_FEATURES = {
  MAX_ATTACHMENTS: 'max_attachments', // Maximum number of attachments per message
  RECURRING_MESSAGES: 'recurring_messages', // Ability to set recurring messages
  RETENTION_DAYS: 'retention_days', // Days to keep sent messages visible
  ACTIVE_MESSAGES: 'active_messages', // Maximum number of active scheduled messages
  UNLIMITED_MESSAGES: 'unlimited_messages', // No limit on number of messages
  REMOVE_BRANDING: 'remove_branding', // Remove "Sent with Delayed Send" branding
  TEMPLATES: 'templates', // Access to reusable templates
}

export const SUBSCRIPTION_PLANS = {
  FREE: {
    features: [], // No premium features in free plan
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 0, // No attachments in free plan
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 7, // Only 7 days retention
      [SUBSCRIPTION_FEATURES.ACTIVE_MESSAGES]: 5, // Only 5 scheduled messages per month
    },
  },
  PRO: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_MESSAGES,
      SUBSCRIPTION_FEATURES.UNLIMITED_MESSAGES,
      SUBSCRIPTION_FEATURES.REMOVE_BRANDING,
      SUBSCRIPTION_FEATURES.TEMPLATES,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 10,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 90,
      [SUBSCRIPTION_FEATURES.ACTIVE_MESSAGES]: 999999, // Effectively unlimited
    },
    pricing: {
      monthly: {
        price: 3,
        currency: 'USD',
        interval: 'month',
      },
      yearly: {
        price: 20,
        currency: 'USD',
        interval: 'year',
      },
    },
  },
  TEAM: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_MESSAGES,
      SUBSCRIPTION_FEATURES.UNLIMITED_MESSAGES,
      SUBSCRIPTION_FEATURES.REMOVE_BRANDING,
      SUBSCRIPTION_FEATURES.TEMPLATES,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 10,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 90,
      [SUBSCRIPTION_FEATURES.ACTIVE_MESSAGES]: 999999, // Effectively unlimited
    },
    pricing: {
      monthly: {
        price: 2,
        currency: 'USD',
        interval: 'month',
        minSeats: 2,
      },
      yearly: {
        price: 15,
        currency: 'USD',
        interval: 'year',
        minSeats: 2,
      },
    },
  },
  GIFT: {
    features: [
      SUBSCRIPTION_FEATURES.RECURRING_MESSAGES,
      SUBSCRIPTION_FEATURES.UNLIMITED_MESSAGES,
      SUBSCRIPTION_FEATURES.REMOVE_BRANDING,
    ],
    limits: {
      [SUBSCRIPTION_FEATURES.MAX_ATTACHMENTS]: 5,
      [SUBSCRIPTION_FEATURES.RETENTION_DAYS]: 30,
      [SUBSCRIPTION_FEATURES.ACTIVE_MESSAGES]: 100,
    },
  },
}
